import { t } from 'i18next';
import { AnyAction } from 'redux';
import { put, call, takeLatest, retry } from '@redux-saga/core/effects';
import { error, success } from '@innovyze/stylovyze';
import {
	listDeteriorationModels,
	listDeteriorationModelsResolved,
	listDeteriorationModelsRejected,
	getDeteriorationModelResolved,
	getDeteriorationModelRejected,
	getDeteriorationModel,
	getDeteriorationModelStatus,
	getDeteriorationModelStatusResolved,
	getDeteriorationModelStatusRejected,
	listDeteriorationModelCurveConfigsAction,
	listDeteriorationModelCurveConfigsResolved,
	listDeteriorationModelCurveConfigsRejected,
	getIndustryStandardCurvesResolved,
	getIndustryStandardCurvesAction,
	listDeteriorationModelAssetsResultsAction,
	listDeteriorationModelAssetsResultsResolved,
	listDeteriorationModelAssetsResultsRejected,
	listDeteriorationModelGroupsResultsAction,
	listDeteriorationModelGroupsResultsResolved,
	listDeteriorationModelGroupsResultsRejected,
	getGlobalUndefinedCountAction,
	getGlobalUndefinedCountResolved,
	getGlobalUndefinedCountRejected,
	updatePublishedStatus,
	updatePublishedStatusResolved,
	updatePublishedStatusRejected,
} from '@Actions/deteriorationModels.actions';
import {
	getDeteriorationModels,
	GetDeteriorationModelsResponse,
	getDeteriorationModelById,
	GetDeteriorationModelResponse,
	listDeteriorationModelCurveConfigs,
	GetDeteriorationModelCurveConfigsResponse,
	getIndustryStandardCurves,
	GetIndustryStandardCurvesResponse,
	getDeteriorationModelAssetsResults,
	getDeteriorationModelGroupsResults,
	GetDeteriorationModelAssetsResultsResponse,
	GetDeteriorationModelGroupsResultsResponse,
	getGlobalUndefinedCount,
	updatePublishedStatus as updatePublishedStatusService,
} from '@Services/deteriorationModels.services';
import {
	DeteriorationModelCard,
	deteriorationModelStatusToStatusType,
	mapCurveMethodResponseToCurveMethod,
	CurveMethod,
	WeibullFormulation,
} from '@Types';
import { AxiosResponse } from 'axios';
import { Row } from '@innovyze/lib-am-common';
import { convertFilterToApiRange, mapApiResultToRow } from '@Components/deteriorationModel/utils/failureProbability';

export function* listDeteriorationModelsSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelsResponse> = yield retry(
			5,
			15000,
			getDeteriorationModels,
			action.payload,
		);
		yield put(
			listDeteriorationModelsResolved({
				pagination: payload.data.pagination,
				data: payload.data.data.map(
					dm =>
						({
							id: dm.id,
							tenantId: dm.tenant_id,
							name: dm.name,
							assetGroupSetId: dm.asset_group_set_id,
							failureDefinitionId: dm.failure_definition_id,
							failureGradeConfigId: dm.failure_grade_config_id,
							status: deteriorationModelStatusToStatusType(dm.status),
							createdBy: dm.created_by,
							createdAt: dm.created_at,
							trainedBy: dm.trained_by,
							trainedAt: dm.trained_at,
							latestTrainResultsId: dm.latest_train_results_id,
							updatedBy: dm.updated_by,
							updatedAt: dm.updated_at,
							latestCurveConfigsId: dm.latest_curve_configs_id,
							runBy: dm.run_by,
							runAt: dm.run_at,
							latestRunResultsId: dm.latest_run_results_id,
							published: dm.published,
						} as DeteriorationModelCard),
				),
			}),
		);
	} catch (e) {
		console.error('Error retrieving deterioration models:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			listDeteriorationModelsRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to list deterioration models')));
	}
}

function* watchListDeteriorationModelsSaga() {
	yield takeLatest(listDeteriorationModels, listDeteriorationModelsSaga);
}

export function* getDeteriorationModelSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelResponse> = yield retry(
			5,
			15000,
			getDeteriorationModelById,
			action.payload.id,
		);
		const data = payload.data.data;
		yield put(
			getDeteriorationModelResolved({
				id: data.id,
				tenantId: data.tenant_id,
				name: data.name,
				assetGroupSetId: data.asset_group_set_id,
				failureDefinitionId: data.failure_definition_id,
				failureGradeConfigId: data.failure_grade_config_id,
				status: deteriorationModelStatusToStatusType(data.status, true),
				createdBy: data.created_by,
				createdAt: data.created_at,
				trainedBy: data.trained_by,
				trainedAt: data.trained_at,
				latestTrainResultsId: data.latest_train_results_id,
				updatedBy: data.updated_by,
				updatedAt: data.updated_at,
				latestCurveConfigsId: data.latest_curve_configs_id,
				runBy: data.run_by,
				runAt: data.run_at,
				latestRunResultsId: data.latest_run_results_id,
				published: data.published,
				systemType: data.system_type,
				assetType: data.asset_type,
				weibullFormulation: (() => {
					const known: string[] = [WeibullFormulation.TWO_PARAMETER, WeibullFormulation.THREE_PARAMETER];
					if (data.weibull_formulation && !known.includes(data.weibull_formulation)) {
						console.warn(
							`Unrecognized weibull_formulation value from API: "${data.weibull_formulation}", defaulting to TWO_PARAMETER`,
						);
					}
					return data.weibull_formulation === WeibullFormulation.THREE_PARAMETER
						? WeibullFormulation.THREE_PARAMETER
						: WeibullFormulation.TWO_PARAMETER;
				})(),
			}),
		);
	} catch (e) {
		console.error('Error retrieving deterioration model:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			getDeteriorationModelRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to get deterioration model')));
	}
}

function* watchGetDeteriorationModelSaga() {
	yield takeLatest(getDeteriorationModel, getDeteriorationModelSaga);
}

export function* getDeteriorationModelStatusSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelResponse> = yield retry(
			5,
			15000,
			getDeteriorationModelById,
			action.payload.id,
		);
		const data = payload.data.data;
		yield put(
			getDeteriorationModelStatusResolved({
				id: data.id,
				status: deteriorationModelStatusToStatusType(data.status, true),
			}),
		);
	} catch (e) {
		console.error('Error retrieving deterioration model status:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			getDeteriorationModelStatusRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		// Note: Not showing error toast for status polling to avoid spam
	}
}

function* watchGetDeteriorationModelStatusSaga() {
	yield takeLatest(getDeteriorationModelStatus, getDeteriorationModelStatusSaga);
}

const mapWeibullFormulationResponse = (raw?: string): WeibullFormulation | undefined => {
	if (!raw) {
		return undefined;
	}
	const known: string[] = [WeibullFormulation.TWO_PARAMETER, WeibullFormulation.THREE_PARAMETER];
	if (!known.includes(raw)) {
		console.warn(`Unrecognized weibull_formulation value from API: "${raw}", defaulting to TWO_PARAMETER`);
		return WeibullFormulation.TWO_PARAMETER;
	}
	return raw === WeibullFormulation.THREE_PARAMETER
		? WeibullFormulation.THREE_PARAMETER
		: WeibullFormulation.TWO_PARAMETER;
};

export function* listDeteriorationModelCurveConfigsSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelCurveConfigsResponse> = yield retry(
			5,
			15000,
			listDeteriorationModelCurveConfigs,
			action.payload,
		);
		const data = payload.data;
		yield put(
			listDeteriorationModelCurveConfigsResolved({
				response: {
					pagination: data.pagination,
					data: data.data.map(dm => ({
						id: dm._id,
						assetGroupId: dm.asset_group_id,
						assetGroupName: dm.asset_group_name,
						assetGroupDescription: dm.asset_group_description,
						assetCount: dm.asset_count.toString(),
						failureCount: dm.failure_count.toString(),
						selectedCurveMethod: mapCurveMethodResponseToCurveMethod(dm.selected_curve_method.method),
					})) as Row[],
				},
				curveConfigs: data.data.map(dm => {
					const curveMethod = mapCurveMethodResponseToCurveMethod(dm.selected_curve_method.method);
					const partialResponse = {
						curveConfigId: dm._id,
						selectedCurveMethod: curveMethod,
						reference: null,
						data: null,
					};

					switch (curveMethod) {
						case CurveMethod.FAILURE_DEFINITION:
							return {
								...partialResponse,
								data: null,
								reference: dm.selected_curve_method.model ?? null,
								weibullFormulation: mapWeibullFormulationResponse(dm.selected_curve_method.weibull_formulation),
							};
						case CurveMethod.CUSTOM_CURVE:
							return {
								...partialResponse,
								data:
									dm.selected_curve_method.custom_data?.map((d: { age: number; survival_probability: number }) => ({
										age: d.age,
										survivalProbability: d.survival_probability * 100,
									})) ?? null,
								reference: null,
								weibullFormulation: mapWeibullFormulationResponse(dm.selected_curve_method.weibull_formulation),
							};
						case CurveMethod.INDUSTRY_CURVE:
							return {
								...partialResponse,
								data: null,
								reference: dm.selected_curve_method.industry_curve_key ?? null,
							};
						case CurveMethod.ONE_EXPECTED_LIFE:
							return {
								...partialResponse,
								data: dm.selected_curve_method.life_expectancy ?? null,
								reference: null,
							};
						case CurveMethod.COPIED_CURVE:
							return {
								...partialResponse,
								data: null,
								reference: dm.selected_curve_method.curve_config_id_reference ?? null,
							};
						default:
							return {
								...partialResponse,
								data: null,
							};
					}
				}),
			}),
		);
	} catch (e) {
		console.error('Error retrieving deterioration model curve configs:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			listDeteriorationModelCurveConfigsRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to list deterioration model curve configs')));
	}
}

function* watchListDeteriorationModelCurveConfigsSaga() {
	yield takeLatest(listDeteriorationModelCurveConfigsAction, listDeteriorationModelCurveConfigsSaga);
}

export function* getIndustryStandardCurvesSaga() {
	try {
		const payload: AxiosResponse<GetIndustryStandardCurvesResponse> = yield call(getIndustryStandardCurves);
		yield put(
			getIndustryStandardCurvesResolved(
				payload.data.data.map(curve => ({
					displayName: curve.display_name,
					key: curve.key,
				})),
			),
		);
	} catch (e) {
		console.error('Error retrieving industry standard curves:', e);
		yield put(error(t('Unable to retrieve industry standard curves')));
	}
}

function* watchGetIndustryStandardCurvesSaga() {
	yield takeLatest(getIndustryStandardCurvesAction, getIndustryStandardCurvesSaga);
}

export function* listDeteriorationModelAssetsResultsSaga(action: AnyAction) {
	try {
		const request = { ...action.payload };

		// Convert failure probability filter from display (0-100) to API (0-1) range
		if (request.filter?.failureProbability) {
			request.filter = {
				...request.filter,
				failureProbability: convertFilterToApiRange(request.filter.failureProbability),
			};
		}

		const payload: AxiosResponse<GetDeteriorationModelAssetsResultsResponse> = yield retry(
			5,
			15000,
			getDeteriorationModelAssetsResults,
			request,
		);
		const data = payload.data;
		yield put(
			listDeteriorationModelAssetsResultsResolved({
				pagination: data.pagination,
				data: data.data.map(mapApiResultToRow) as unknown as Row[],
			}),
		);
	} catch (e) {
		console.error('Error retrieving deterioration model assets results:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			listDeteriorationModelAssetsResultsRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to list deterioration model assets results')));
	}
}

function* watchListDeteriorationModelAssetsResultsSaga() {
	yield takeLatest(listDeteriorationModelAssetsResultsAction, listDeteriorationModelAssetsResultsSaga);
}

export function* listDeteriorationModelGroupsResultsSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelGroupsResultsResponse> = yield retry(
			5,
			15000,
			getDeteriorationModelGroupsResults,
			action.payload,
		);
		const data = payload.data;
		yield put(
			listDeteriorationModelGroupsResultsResolved({
				pagination: data.pagination,
				data: data.data.map(result => ({
					id: result._id,
					assetGroupId: result.asset_group_id,
					assetGroupName: result.asset_group_name,
					assetGroupDescription: result.asset_group_description,
					assetCount: result.asset_count?.toFixed(0),
					failureCount: result.failure_count?.toFixed(0),
					deteriorationMethod: mapCurveMethodResponseToCurveMethod(result.deterioration_method),
					thresholdAge: result.threshold_age ?? null,
					fallback: result.deterioration_params?.fallback ?? false,
					// Undefined for runs from before I360A-2882 — the UI treats that as "no reason given".
					fallbackReason: result.deterioration_params?.fallback_reason,
					actualFormulation: result.deterioration_params?.actual_formulation,
					weibullParams: result.deterioration_params?.params,
				})) as unknown as Row[],
			}),
		);
	} catch (e) {
		console.error('Error retrieving deterioration model groups results:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			listDeteriorationModelGroupsResultsRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to list deterioration model groups results')));
	}
}

function* watchListDeteriorationModelGroupsResultsSaga() {
	yield takeLatest(listDeteriorationModelGroupsResultsAction, listDeteriorationModelGroupsResultsSaga);
}

export function* getGlobalUndefinedCountSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<GetDeteriorationModelCurveConfigsResponse> = yield retry(
			5,
			15000,
			getGlobalUndefinedCount,
			action.payload.modelId,
		);
		yield put(
			getGlobalUndefinedCountResolved({
				response: {
					pagination: payload.data.pagination,
					data: [],
				},
			}),
		);
	} catch (e) {
		console.error('Error retrieving global undefined count:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			getGlobalUndefinedCountRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to get global undefined count')));
	}
}

function* watchGetGlobalUndefinedCountSaga() {
	yield takeLatest(getGlobalUndefinedCountAction, getGlobalUndefinedCountSaga);
}

export function* updatePublishedStatusSaga(action: AnyAction) {
	try {
		yield call(updatePublishedStatusService, {
			modelId: action.payload.modelId,
			published: action.payload.published,
		});
		yield put(
			updatePublishedStatusResolved({
				modelId: action.payload.modelId,
				published: action.payload.published,
			}),
		);
		yield put(
			success(
				t(
					action.payload.published
						? 'Deterioration model published successfully'
						: 'Deterioration model unpublished successfully',
				),
			),
		);
	} catch (e) {
		console.error('Error updating published status:', e);
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const { response } = e;
		yield put(
			updatePublishedStatusRejected({
				code: response?.status ?? -1,
				text: response?.statusText ?? t('No additional information'),
			}),
		);
		yield put(error(t('Unable to update published status')));
	}
}

function* watchUpdatePublishedStatusSaga() {
	yield takeLatest(updatePublishedStatus, updatePublishedStatusSaga);
}

const sagaArray = [
	watchListDeteriorationModelsSaga(),
	watchGetDeteriorationModelSaga(),
	watchGetDeteriorationModelStatusSaga(),
	watchListDeteriorationModelCurveConfigsSaga(),
	watchGetIndustryStandardCurvesSaga(),
	watchListDeteriorationModelAssetsResultsSaga(),
	watchListDeteriorationModelGroupsResultsSaga(),
	watchGetGlobalUndefinedCountSaga(),
	watchUpdatePublishedStatusSaga(),
];

export default sagaArray;
